/**
 * Animation vocabulary for game & game-lite.
 * Single source of truth for durations, easings, and effect builders.
 *
 * All fx functions return plain GSAP prop objects — no side effects.
 * Import and spread directly into gsap.to / gsap.fromTo / timeline calls.
 *
 * Pure duration/easing tokens live in `./anim-tokens.js` (no gsap import)
 * and are re-exported here so existing consumers stay unchanged. Files
 * that only need tokens (framer-motion consumers, App shell) should
 * import from `./anim-tokens` directly to avoid pulling gsap into
 * their bundle.
 */
import gsap from 'gsap';
import { DUR, EASE, EASE_BEZIER, prefersReducedMotion, liteMotion } from './anim-tokens';
export { DUR, EASE, EASE_BEZIER, prefersReducedMotion, liteMotion };

// ─── Stagger intervals (seconds) ────────────────────────────────────────────

const STAGGER_ENTER = 0.07
const STAGGER_EXIT  = 0.04

// ─── Internal step constants (reused across fx functions) ───────────────────
// Defined outside the object so wrongAnswer() can spread them without
// referencing `this` (which breaks when called as fx.reject()).

const REJECT_STEPS = [
  { x: -9, duration: 0.06, ease: 'power3.out' },
  { x:  7, duration: 0.07, ease: 'sine.inOut' },
  { x: -5, duration: 0.06, ease: 'sine.inOut' },
  { x:  3, duration: 0.06, ease: 'sine.inOut' },
  { x:  0, duration: 0.08, ease: 'power2.out' },
]

// ─── Effect builders ────────────────────────────────────────────────────────

export const fx = {
  /**
   * Slide up from below with stagger by index.
   * Use with gsap.fromTo(el, result.from, { ...result.to, duration, delay, ease })
   */
  slideInUp(index = 0) {
    return {
      from:       { y: 28, opacity: 0 },
      to:         { y: 0, opacity: 1 },
      duration:   DUR.med,
      delay:      index * STAGGER_ENTER,
      ease:       EASE.snap,
      clearProps: 'y,opacity',
    }
  },

  /**
   * Scale pop — peaks above 1 then snaps back.
   * Use with gsap.to(el, fx.pop())
   */
  pop() {
    return {
      keyframes: [{ scale: 1.1 }, { scale: 1 }],
      duration:  0.22,
      ease:      EASE.snap,
    }
  },

  /**
   * Elastic bounce for interactive elements (button pops, card toggles, dimmed taps).
   * @param {number} peak - peak scale (default 1.1)
   * Returns array of 2 tween-step objects:
   *   fx.bounce(1.2).forEach(step => tl.to(el, step))
   */
  bounce(peak = 1.1) {
    return [
      { scale: peak, duration: 0.10, ease: EASE.snap   },
      { scale: 1,    duration: 0.35, ease: EASE.spring },
    ]
  },

  /**
   * Ring glow for selected state.
   * Use with gsap.to(el, fx.glow())
   */
  glow(color = 'var(--color-wine-secondary, #9b2335)') {
    return {
      boxShadow: `0 0 0 3px ${color}`,
      duration:  DUR.fast,
      ease:      EASE.smooth,
    }
  },

  /**
   * Selected-state glow pulse: rings in then fades out.
   * @param {number} r,g,b  RGB components (default: wine-secondary 155,35,53)
   * Returns { from, to, out } for a 3-step timeline:
   *   tl.fromTo(el, result.from, result.to)
   *     .to(el, result.out)
   *     .set(el, { clearProps: 'boxShadow' })
   */
  glowPulse(r = 155, g = 35, b = 53) {
    const zero = `0 0 0 0px rgba(${r},${g},${b},0)`
    return {
      from: { boxShadow: zero },
      to:   { boxShadow: `0 0 0 5px rgba(${r},${g},${b},0.7)`, duration: 0.18, ease: 'power2.out' },
      out:  { boxShadow: zero, duration: 0.40 },
    }
  },

  /**
   * Slide out upward and fade, staggered top-to-bottom.
   * Use with gsap.to(el, fx.slideOut(i))
   */
  slideOut(index = 0) {
    return {
      y:        -20,
      opacity:  0,
      duration: DUR.fast,
      delay:    index * STAGGER_EXIT,
      ease:     EASE.in,
    }
  },

  /**
   * Wiggle/shake — returns an array of tween step objects.
   * Apply via a GSAP timeline: steps.forEach(step => tl.to(el, step))
   */
  wiggle() {
    return [
      { x: -4, rotation: -1,   duration: 0.05, ease: 'sine.inOut' },
      { x:  4, rotation:  1,   duration: 0.05, ease: 'sine.inOut' },
      { x: -3, rotation: -0.8, duration: 0.05, ease: 'sine.inOut' },
      { x:  3, rotation:  0.8, duration: 0.05, ease: 'sine.inOut' },
      { x:  0, rotation:  0,   duration: 0.08, ease: 'power1.out' },
    ]
  },

  /**
   * Reject shake — signals "no" with aggressive horizontal movement, no rotation.
   * Distinct from wiggle: higher amplitude, quicker, no rotation.
   * Returns an array of tween step objects for a GSAP timeline.
   */
  reject() {
    return [...REJECT_STEPS]
  },

  /**
   * Correct-answer reveal: scale pop + brightness flash, elastic settle.
   * Returns array of 3 tween-step objects for a GSAP timeline:
   *   fx.correctAnswer().forEach(step => tl.to(el, step))
   */
  correctAnswer() {
    return [
      { scale: 1.07, filter: 'brightness(1.55)', duration: 0.14, ease: EASE.snap    },
      { scale: 0.97, filter: 'brightness(1)',    duration: 0.09, ease: 'power3.in'  },
      { scale: 1,                                duration: 0.40, ease: EASE.spring  },
    ]
  },

  /**
   * Wrong-answer reveal: brightness flash wrapping the reject shake.
   * Returns array of tween-step objects for a GSAP timeline:
   *   fx.wrongAnswer().forEach(step => tl.to(el, step))
   */
  wrongAnswer() {
    return [
      { filter: 'brightness(1.4)', duration: DUR.micro },
      ...REJECT_STEPS,
      { filter: 'brightness(1)',   duration: 0.12      },
    ]
  },

  /**
   * Badge pop-in entrance animation.
   * Returns { from, to } for tl.fromTo(badge, result.from, result.to).
   * Follow with fx.badgeSettle() for the shimmy + scale settle.
   */
  badgeEntrance() {
    return {
      from: { scale: 0.50, opacity: 0, y: 8, rotate: -8 },
      to:   { scale: 1.15, opacity: 1, y: 0, rotate: 0, duration: 0.28, ease: 'back.out(1.8)' },
    }
  },

  /**
   * Badge micro-shimmy + scale settle (run as tl.to steps after badgeEntrance).
   * Returns array of 2 tween steps:
   *   fx.badgeSettle().forEach(step => tl.to(badge, step))
   */
  badgeSettle() {
    return [
      { x: 3, duration: 0.06, repeat: 6, yoyo: true, ease: 'power1.inOut' },
      { scale: 1, duration: 0.14, ease: 'power2.out' },
    ]
  },

  /**
   * Badge continuous jiggle loop (run as a separate gsap.to after entrance).
   * Returns gsap.to props:
   *   gsap.to(badge, fx.badgeJiggle())
   */
  badgeJiggle() {
    if (liteMotion()) return { x: 0, duration: 0 } // no infinite loops on lite (TAS-311)
    return {
      x: 3, duration: 0.06, repeat: -1, repeatDelay: 2.3,
      yoyo: true, ease: 'power1.inOut', delay: 0.7,
    }
  },

  /**
   * Floating point badge pulse loop (+1 / +N badges on power icons).
   * Returns { reset, steps } for a repeating timeline:
   *   const { reset, steps } = fx.badgePulse()
   *   const tl = gsap.timeline({ repeat: -1, repeatDelay: 2.1 })
   *   tl.fromTo(badge, reset, steps[0])
   *   steps.slice(1).forEach(step => tl.to(badge, step))
   */
  badgePulse() {
    return {
      reset: { opacity: 0, scale: 0.55, y: 8 },
      steps: [
        { opacity: 1, scale: 1.08, y: 0,  duration: 0.24, ease: 'back.out(1.8)' },
        { scale: 1,                        duration: 0.14, ease: 'power2.out'    },
        { opacity: 1,                      duration: 0.40, ease: 'power1.out'    },
        { opacity: 0, y: -6,               duration: 0.24, ease: 'power1.in'     },
      ],
    }
  },

  /**
   * Gainer lean: card tilts right to signal rank rise.
   * gsap.set(el, { clearProps: 'x,rotation' }) after onComplete.
   */
  lean() {
    return { x: 6, rotation: 1.5, duration: DUR.fast, ease: EASE.snap };
  },

  /**
   * Loser flinch: card tilts left to signal rank drop.
   * gsap.set(el, { clearProps: 'x,rotation' }) after onComplete.
   */
  flinch() {
    return { x: -6, rotation: -1.5, duration: DUR.fast, ease: EASE.snap };
  },

  /**
   * Crown drop entrance: initial state must be set first via gsap.set(el, { y: -40, opacity: 0 }).
   * Then call gsap.to(el, fx.crownDrop()) to drop in and dissolve.
   */
  crownDrop() {
    return {
      keyframes: [
        { y: 0,   opacity: 1, duration: DUR.med,        ease: EASE.snap   },
        { y: 0,   opacity: 1, duration: DUR.slow * 0.6                    },
        { y: -12, opacity: 0, duration: DUR.med,        ease: EASE.in     },
      ],
    };
  },

  /**
   * Card border fanfare: pulse gold to celebrate the new rank-1 card.
   * gsap.set(el, { clearProps: 'boxShadow' }) after onComplete.
   */
  cardFanfare() {
    if (liteMotion()) {
      // A single soft pulse instead of the animated glow — box-shadow
      // keyframes repaint the whole card every frame (TAS-311).
      return { keyframes: [{ boxShadow: '0 0 12px gold' }, { boxShadow: '0 0 0px gold' }], duration: DUR.med };
    }
    return {
      keyframes: [
        { boxShadow: '0 0 0px gold'  },
        { boxShadow: '0 0 20px gold' },
        { boxShadow: '0 0 0px gold'  },
      ],
      duration: DUR.slow,
    };
  },
}

// ─── Modal open/close (shared by ALL modals) ────────────────────────────────
// One entry, one exit, everywhere: backdrop fades, the card lifts in and
// settles, then inner elements cascade top-to-bottom. Under reduced motion
// the whole thing is a plain fade and items appear in place.

/**
 * Modal entrance timeline.
 * @param {object}   refs
 *   backdrop — overlay element (opacity fade). Optional.
 *   content  — the card (lift + settle). Optional for full-screen prompts.
 *   items    — inner elements to cascade (NodeList/array). Optional.
 * Returns the timeline — kill() it in the effect cleanup.
 */
export function modalIn({ backdrop, content, items = [] } = {}) {
  const itemEls = gsap.utils.toArray(items);
  const tl = gsap.timeline();

  if (prefersReducedMotion()) {
    const targets = [backdrop, content].filter(Boolean);
    if (targets.length) {
      tl.fromTo(targets, { opacity: 0 }, { opacity: 1, duration: DUR.med, ease: EASE.smooth });
    }
    return tl;
  }

  // Initial states land at t=0 so nothing flashes unstyled before its tween.
  if (content)         tl.set(content, { y: 48, opacity: 0, scale: 0.97 }, 0);
  if (itemEls.length)  tl.set(itemEls, { y: 14, opacity: 0 }, 0);

  if (backdrop) {
    tl.fromTo(backdrop, { opacity: 0 }, { opacity: 1, duration: DUR.med, ease: EASE.out }, 0);
  }
  if (content) {
    tl.to(content, { y: 0, opacity: 1, scale: 1, duration: DUR.med, ease: EASE.snap }, 0.05);
  }
  if (itemEls.length) {
    tl.to(itemEls, {
      y: 0, opacity: 1, duration: DUR.med, ease: EASE.snap,
      stagger: STAGGER_ENTER, clearProps: 'y,opacity',
    }, content ? 0.18 : 0.08);
  }
  return tl;
}

/**
 * Track a swipe-to-dismiss drag in progress.
 *
 * This must go through gsap.set rather than writing `style.transform`
 * directly. GSAP caches an element's transform state, so a raw style write is
 * invisible to it — the follow-up `gsap.to(el, { y: 0 })` would believe the
 * sheet was already at rest and snap instead of easing, and a dismissal would
 * jump before animating out (codex pair review, 2026-08-24). gsap.set is a
 * synchronous write, so this is still cheap enough for every pointermove.
 */
export function dragSheetTo(sheet, backdrop, travel) {
  if (sheet) gsap.set(sheet, { y: travel });
  // Fade the backdrop as the sheet leaves, but never to nothing — the screen
  // behind is mid-game and shouldn't reappear at full strength mid-gesture.
  if (backdrop) gsap.set(backdrop, { opacity: Math.max(0.25, 1 - travel / 320) });
}

/**
 * Release a drag that didn't travel far enough — the sheet settles back to
 * rest. Returns the timeline so the caller can kill it if a second drag or a
 * close starts before it lands.
 */
export function sheetSpringBack(sheet, backdrop) {
  const tl = gsap.timeline();
  if (sheet) tl.to(sheet, { y: 0, duration: DUR.med, ease: EASE.spring }, 0);
  if (backdrop) tl.to(backdrop, { opacity: 1, duration: DUR.fast, ease: EASE.smooth }, 0);
  return tl;
}

/**
 * A press that has to feel like it did something — the nudge button.
 *
 * A nudge produces no visible result on the presser's own screen: the effect
 * lands on somebody else's phone. Without a reaction here the host presses,
 * sees nothing, and presses again (Mel, 2026-08-25). This is the receipt.
 */
export function nudgePress(el) {
  const tl = gsap.timeline();
  if (!el) return tl;
  // liteMotion, not prefersReducedMotion: this is DECORATIVE, so it also has to
  // stand down on hardware that can't afford it (TAS-311). Standing down means
  // NOTHING — the earlier version still ran an opacity tween, which is the
  // cost we were trying to avoid (codex pair review). The button's own
  // disabled/"Nudged" state is the feedback on this tier.
  if (liteMotion()) return tl;
  tl.fromTo(el, { scale: 1 }, { scale: 0.92, duration: 0.08, ease: EASE.snap })
    .to(el, { scale: 1, duration: 0.32, ease: EASE.spring })
    // Sideways, because a nudge is a prod rather than a bounce.
    .fromTo(el, { x: 0 }, { x: 3, duration: 0.06, ease: EASE.snap, yoyo: true, repeat: 3, clearProps: 'x' }, 0.05);
  return tl;
}

/**
 * The lesson chip asking to be noticed.
 *
 * The chip is deliberately small and out of the way, which is the point — and
 * also the risk: a player looking at the answer grid never sees it. This is a
 * periodic tap on the shoulder, not a loop: a short rock, and the bulb catching
 * the light, every few seconds.
 *
 * Decorative, so it does nothing at all on the lite tier — including not
 * scheduling the interval, since a timer that wakes up to do nothing is still
 * a timer (TAS-311).
 *
 * @returns {() => void} stop — call on unmount.
 */
export function lessonChipNudge(el, glowEl) {
  if (!el || liteMotion()) return () => {};

  const play = () => {
    const tl = gsap.timeline();
    // A rock rather than a shake: the chip is a resting object being nudged,
    // and a buzz reads as an error state.
    tl.to(el, { rotation: -1.6, duration: 0.09, ease: EASE.snap })
      .to(el, { rotation: 1.6, duration: 0.12, ease: EASE.snap })
      .to(el, { rotation: 0, duration: 0.4, ease: EASE.spring });
    if (glowEl) {
      // The bulb catches the light on the way through — brightness rather than
      // a added glow layer, so it costs one filter and no extra paint.
      tl.fromTo(glowEl,
        { filter: 'brightness(1)' },
        { filter: 'brightness(1.45)', duration: 0.22, ease: EASE.smooth }, 0)
        .to(glowEl, { filter: 'brightness(1)', duration: 0.5, ease: EASE.smooth }, 0.22);
    }
    return tl;
  };

  // First one lands a beat after the chip settles, then every few seconds.
  let tl = null;
  const first = setTimeout(() => { tl = play(); }, 1200);
  const id = setInterval(() => { tl = play(); }, 6000);
  return () => {
    clearTimeout(first);
    clearInterval(id);
    tl?.kill();
    gsap.set([el, glowEl].filter(Boolean), { clearProps: 'rotation,filter' });
  };
}

/**
 * A row whose content changed under the reader — one player moving from one
 * clue to the next while the host is watching the list.
 *
 * Deliberately quiet: this fires while the host is reading, and anything more
 * than a settle would pull their eye off whoever they were looking at.
 */
export function statusChange(el) {
  const tl = gsap.timeline();
  if (!el) return tl;
  // Decorative — a settle on a line the host is already reading.
  if (liteMotion()) return tl;
  tl.fromTo(el,
    { opacity: 0, y: -4 },
    { opacity: 1, y: 0, duration: DUR.med, ease: EASE.snap, clearProps: 'opacity,y' });
  return tl;
}

/**
 * Paging swap inside an already-open sheet — used when the point receipt flips
 * from one player to the next.
 *
 * Enter-only, deliberately: the content is swapped by React the moment the
 * index changes, so animating OUT first would mean holding the old player on
 * screen behind a timeline and re-rendering in a completion callback. That
 * races with a fast double-tap on Next. Animating only the arrival is honest
 * about what happened — new card slides in from the side you travelled toward
 * — and a rapid tap simply restarts it.
 *
 * @param {Element} container — the region whose content just changed
 * @param {number} direction  — +1 moving to the next player, -1 to the previous
 * @param {Array}  items      — rows to stagger in behind the container
 */
export function pageSwapIn(container, direction = 1, { items = [] } = {}) {
  const tl = gsap.timeline();
  if (!container) return tl;
  const itemEls = gsap.utils.toArray(items);

  if (prefersReducedMotion()) {
    tl.fromTo(container, { opacity: 0 }, { opacity: 1, duration: DUR.fast, ease: EASE.smooth });
    return tl;
  }

  // Travelling to the NEXT player means the new card comes from the right.
  const from = direction >= 0 ? 26 : -26;
  tl.fromTo(container,
    { x: from, opacity: 0 },
    { x: 0, opacity: 1, duration: DUR.med, ease: EASE.snap, clearProps: 'x,opacity' }, 0);
  if (itemEls.length) {
    tl.fromTo(itemEls,
      { y: 8, opacity: 0 },
      { y: 0, opacity: 1, duration: DUR.fast, ease: EASE.snap,
        stagger: 0.03, clearProps: 'y,opacity' }, 0.06);
  }
  return tl;
}

/**
 * Modal exit timeline — quick, gets out of the way.
 * @param {object} refs — backdrop / content as in modalIn, plus onComplete.
 */
export function modalOut({ backdrop, content, onComplete } = {}) {
  const tl = gsap.timeline({ onComplete });

  if (prefersReducedMotion()) {
    const targets = [content, backdrop].filter(Boolean);
    if (targets.length) tl.to(targets, { opacity: 0, duration: DUR.fast, ease: EASE.in });
    return tl;
  }

  if (content)  tl.to(content, { y: 32, opacity: 0, scale: 0.97, duration: DUR.fast, ease: EASE.in });
  if (backdrop) tl.to(backdrop, { opacity: 0, duration: DUR.fast, ease: EASE.in }, '<');
  return tl;
}

/**
 * Spawn subtle ⚡ emojis that pop in along the screen edges then fade out.
 * @param {number} [count=7]
 */
export function edgeLightning(count = 7) {
  if (liteMotion()) return; // decorative — skipped on lite tier (TAS-311)
  const W = window.innerWidth;
  const H = window.innerHeight;
  for (let i = 0; i < count; i++) {
    const el = document.createElement('span');
    el.textContent = '⚡';
    const size = 0.75 + Math.random() * 0.55;
    el.style.cssText = `position:fixed;pointer-events:none;z-index:9998;font-size:${size}rem;opacity:0;user-select:none;line-height:1;`;
    const edge = i % 4;
    const pad = 24;
    if (edge === 0) { el.style.left = `${pad + Math.random() * (W - pad * 2)}px`; el.style.top = `${4 + Math.random() * pad}px`; }
    if (edge === 1) { el.style.right = `${4 + Math.random() * pad}px`; el.style.top = `${pad + Math.random() * (H - pad * 2)}px`; }
    if (edge === 2) { el.style.left = `${pad + Math.random() * (W - pad * 2)}px`; el.style.bottom = `${4 + Math.random() * pad}px`; }
    if (edge === 3) { el.style.left = `${4 + Math.random() * pad}px`; el.style.top = `${pad + Math.random() * (H - pad * 2)}px`; }
    document.body.appendChild(el);
    const delay = 0.05 + Math.random() * 0.35;
    gsap.timeline({ delay })
      .to(el, { opacity: 0.55 + Math.random() * 0.35, scale: 1, duration: 0.1, ease: 'power2.out' })
      .to(el, { opacity: 0, duration: 0.3, ease: 'power1.in', onComplete: () => el.remove() });
  }
}

/**
 * Burst emoji particles outward from a DOM element.
 */
export function emojiExplosion(originEl, emoji, { imageSrc, count = 14, minDist = 45, maxDist = 85, duration = 0.5 } = {}) {
  if (!originEl || !emoji || liteMotion()) return; // decorative (TAS-311)
  const rect = originEl.getBoundingClientRect();
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const particles = Array.from({ length: count }, () => {
    const el = document.createElement('span');
    if (imageSrc) {
      const image = document.createElement('img');
      image.src = imageSrc;
      image.alt = '';
      image.style.cssText = 'width:1em;height:1em;object-fit:contain;';
      el.appendChild(image);
    } else {
      el.textContent = emoji;
    }
    el.style.cssText = `position:fixed;pointer-events:none;z-index:9999;font-size:1.4rem;line-height:1;left:${cx}px;top:${cy}px;transform:translate(-50%,-50%);user-select:none;`;
    document.body.appendChild(el);
    return el;
  });
  particles.forEach((el, i) => {
    const angle = (i / count) * Math.PI * 2;
    const dist = minDist + Math.random() * (maxDist - minDist);
    gsap.to(el, {
      x: Math.cos(angle) * dist,
      y: Math.sin(angle) * dist,
      scale: 0.2 + Math.random() * 0.5,
      opacity: 0,
      rotation: (Math.random() - 0.5) * 180,
      duration: duration + Math.random() * 0.15,
      ease: 'power2.out',
      onComplete: () => el.remove(),
    });
  });
}

/**
 * Pop-bounce a button/element on selection.
 */
export function selectionPop(el, { peakScale = 1.35, restScale = 1.1 } = {}) {
  if (!el) return;
  gsap.timeline()
    .to(el, { scale: peakScale, duration: 0.1, ease: 'power3.out' })
    .to(el, { scale: restScale, duration: 0.35, ease: 'elastic.out(1,0.4)' });
}

// The opening's controller stays behind the shared animation entry point.
export { createGiftTear } from './giftTear';


// The prize code arriving once the box is open (Mel, 2026-08-26: "the code
// details should appear and animate in after the box is opened and nudged to
// be visible"). Two tiers, per the rule in CLAUDE.md:
//   - The APPEARANCE is essential: with reduced motion it still appears, as a
//     plain fade, so nobody is left staring at an empty footer.
//   - The settle and the glow sweep are decorative: dropped on the lite tier.
// Returns a cleanup that kills the tween and leaves the element at rest.
export function prizeCodeReveal(el, glyphEl) {
  if (!el) return () => {};
  const reduce = typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  if (reduce || liteMotion()) {
    gsap.set(el, { opacity: 1, y: 0 });
    return () => {};
  }
  const tl = gsap.timeline();
  // Rises into place and overshoots by a hair — an object landing, not a fade.
  tl.fromTo(el,
    { opacity: 0, y: 18, scale: 0.96 },
    { opacity: 1, y: 0, scale: 1, duration: DUR.slow, ease: EASE.spring });
  if (glyphEl) {
    // One pass of light across the characters, then rest. Once — a code that
    // keeps twinkling reads as an advert.
    tl.fromTo(glyphEl,
      { textShadow: '0 0 0px rgba(201,168,76,0)' },
      { textShadow: '0 0 18px rgba(201,168,76,0.85)', duration: 0.28, ease: EASE.smooth }, '-=0.1')
      .to(glyphEl, { textShadow: '0 0 0px rgba(201,168,76,0)', duration: 0.6, ease: EASE.smooth });
  }
  return () => {
    tl.kill();
    gsap.set([el, glyphEl].filter(Boolean), { clearProps: 'opacity,transform,textShadow' });
  };
}

// ─── Post-game intent (TAS-373) ─────────────────────────────────────────────

// The final page's Next pill moving the card track on by one card.
// ESSENTIAL — the card has to change, so it always happens; under
// prefers-reduced-motion it jumps instead of gliding. A swipe is the browser's
// own scroll-snap and needs nothing from here.
export function slideTrackTo(track, left) {
  if (!track) return;
  const behavior = prefersReducedMotion() ? 'auto' : 'smooth';
  if (typeof track.scrollTo === 'function') track.scrollTo({ left, behavior });
  else track.scrollLeft = left;
}

// Final page arriving: its blocks rise in one after another.
// DECORATIVE — the page is fully visible without it, so it checks liteMotion()
// and does nothing on that tier. Returns a cleanup.
export function postgameEnter(items) {
  const els = gsap.utils.toArray(items);
  if (!els.length || liteMotion()) return () => {};
  const tweens = els.map((el, i) => {
    const s = fx.slideInUp(i);
    return gsap.fromTo(el, s.from, { ...s.to, duration: s.duration, delay: s.delay, ease: s.ease });
  });
  return () => {
    tweens.forEach((t) => t.kill());
    gsap.set(els, { clearProps: 'y,opacity' });
  };
}

// Scoreboard banner arriving under the settled standings. DECORATIVE.
export function bannerRise(el) {
  if (!el || liteMotion()) return () => {};
  const s = fx.slideInUp(0);
  const t = gsap.fromTo(el, s.from, { ...s.to, duration: DUR.slow, ease: s.ease });
  return () => {
    t.kill();
    gsap.set(el, { clearProps: 'y,opacity' });
  };
}
